import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { COMPASS_IMG } from "@/lib/api";
import Layout from "@/components/Layout";
import AuthPage from "@/pages/AuthPage";
import Community from "@/pages/Community";
import Today from "@/pages/Today";
import Journal from "@/pages/Journal";
import Messages from "@/pages/Messages";
import Meetings from "@/pages/Meetings";
import MeetingRoom from "@/pages/MeetingRoom";
import Events from "@/pages/Events";
import Resources from "@/pages/Resources";
import Programs from "@/pages/Programs";
import ProgramDetail from "@/pages/ProgramDetail";
import Library from "@/pages/Library";
import AskAI from "@/pages/AskAI";
import Profile from "@/pages/Profile";
import Upgrade from "@/pages/Upgrade";
import Admin from "@/pages/Admin";
import LegalPage from "@/pages/LegalPage";
import InstallPrompt from "@/components/InstallPrompt";

const FullScreenLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background sea-gradient">
    <img src={COMPASS_IMG} alt="Loading" className="w-20 h-20 rounded-full object-cover compass-spin compass-glow" />
    <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const AuthOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/community" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<AuthOnly><AuthPage /></AuthOnly>} />
            {/* Public legal routes (App Store reviewer requirement) */}
            <Route path="/privacy" element={<LegalPage page="privacy" />} />
            <Route path="/terms" element={<LegalPage page="terms" />} />
            <Route path="/support" element={<LegalPage page="support" />} />
            {/* Full-screen meeting room (no shell) */}
            <Route path="/meetings/room/:id" element={<Protected><MeetingRoom /></Protected>} />

            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/community" element={<Community />} />
              <Route path="/today" element={<Today />} />
              <Route path="/journal" element={<Journal />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/meetings" element={<Meetings />} />
              <Route path="/events" element={<Events />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/programs" element={<Programs />} />
              <Route path="/programs/:id" element={<ProgramDetail />} />
              <Route path="/library" element={<Library />} />
              <Route path="/ask-ai" element={<AskAI />} />
              <Route path="/upgrade" element={<Upgrade />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin" element={<Admin />} />
            </Route>
            <Route path="*" element={<Navigate to="/community" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors theme="dark" />
        <InstallPrompt />
      </AuthProvider>
    </div>
  );
}

export default App;
